import dayjs from 'dayjs';

/** Same fallback the planning board uses when an order has no sorting for the date. */
export const MISSING_PLANNING_SEQUENCE = 999;

export type PlanningRouteSortingLike = {
  date: Date;
  sequence: number;
};

export type PlanningOrderLike = {
  id: string;
  routeSortings: PlanningRouteSortingLike[];
};

export type PlanningStopLike = {
  sequenceIndex: number;
  id?: number;
};

function isInPlanningDay(value: Date, planningDate: Date): boolean {
  const start = dayjs(planningDate).startOf('day');
  const end = dayjs(planningDate).endOf('day');
  const current = dayjs(value);
  const isOnOrAfterStart = current.isAfter(start) || current.isSame(start);
  const isOnOrBeforeEnd = current.isBefore(end) || current.isSame(end);
  return isOnOrAfterStart && isOnOrBeforeEnd;
}

/**
 * Sequence for a planning date, matching the board's start/end-of-day window.
 * If several sortings fall on that day, the lowest sequence wins.
 * Orders without a sorting for that date go last (999).
 */
export function getPlanningRouteSequence(routeSortings: PlanningRouteSortingLike[], planningDate: Date): number {
  const matches = routeSortings
    .filter((sorting) => isInPlanningDay(sorting.date, planningDate))
    .sort((a, b) => a.sequence - b.sequence);
  return matches[0]?.sequence ?? MISSING_PLANNING_SEQUENCE;
}

/** Board columns and the planning PDF both use this compare. */
export function comparePlanningOrders(
  a: { sequence: number; id: string },
  b: { sequence: number; id: string },
): number {
  if (a.sequence !== b.sequence) {
    return a.sequence - b.sequence;
  }
  return a.id.localeCompare(b.id);
}

/**
 * Sort truck orders the same way `/internal/planning-board` displays them:
 * route-sorting sequence for the date, then id when sequences tie.
 */
export function sortPlanningOrders<T extends PlanningOrderLike>(orders: T[], planningDate: Date): T[] {
  return [...orders]
    .map((order) => ({
      order,
      sequence: getPlanningRouteSequence(order.routeSortings, planningDate),
    }))
    .sort((a, b) =>
      comparePlanningOrders({ sequence: a.sequence, id: a.order.id }, { sequence: b.sequence, id: b.order.id }),
    )
    .map((entry) => entry.order);
}

export function compareStopsBySequenceIndex(a: PlanningStopLike, b: PlanningStopLike): number {
  if (a.sequenceIndex !== b.sequenceIndex) {
    return a.sequenceIndex - b.sequenceIndex;
  }
  return (a.id ?? 0) - (b.id ?? 0);
}

/** All stops across order lines, in the same order as the planning-board cards. */
export function flattenStopsBySequenceIndex<T extends PlanningStopLike>(lines: Array<{ stops: T[] }>): T[] {
  return lines.flatMap((line) => line.stops).sort(compareStopsBySequenceIndex);
}
